import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Database, Clock, Download, Settings as SettingsIcon, Building2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const SanctionsPage = () => {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  const [ingestReport, setIngestReport] = useState(null);
  const [csvText, setCsvText] = useState('');

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const [c, s, h] = await Promise.all([
        api.get('/sanctions/config'),
        api.get('/sanctions/stats'),
        api.get('/sanctions/ingest/history?limit=10'),
      ]);
      setConfig(c.data); setStats(s.data); setHistory(h.data.runs || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try { await api.put('/sanctions/config', config); toast.success('Configuration saved.'); load(); }
    catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const handleSaveSchedule = async () => {
    try { const r = await api.put('/sanctions/schedule', { hour: config.schedule.hour, minute: config.schedule.minute }); toast.success('Schedule updated. Next run: ' + (r.data.next_run?.slice(0, 16).replace('T', ' ') || '—')); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleSeedSample = async () => {
    try { const r = await api.post('/sanctions/seed-sample-data'); toast.success(`Seeded ${r.data.counts.ofac + r.data.counts.un + r.data.counts.sa_treasury + r.data.counts.peps} entries`); load(); }
    catch { toast.error('Seed failed'); }
  };

  const handleRunIngest = async () => {
    if (!window.confirm('Run full sanctions ingestion now? This will download the latest OFAC SDN, UN Consolidated, and SA Treasury lists.')) return;
    setIngesting(true);
    setIngestReport(null);
    try { const r = await api.post('/sanctions/ingest/run-now'); setIngestReport(r.data); toast.success(`Ingested ${r.data.total_ingested} entries`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Ingest failed'); }
    setIngesting(false);
  };

  const handleUploadSaTreasury = async () => {
    if (!csvText.trim()) return toast.error('Paste CSV text first');
    try { await api.post('/sanctions/sa-treasury/upload', { csv_text: csvText }); toast.success('SA Treasury CSV stored. Run ingestion to apply.'); setCsvText(''); loadSaTreasury(); }
    catch { toast.error('Upload failed'); }
  };

  const handleUploadSaTreasuryFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) return toast.error('Only .csv files are accepted');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post('/sanctions/sa-treasury/upload-file', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Uploaded ${r.data.filename} (${r.data.rows} rows). Run ingestion to apply.`);
      loadSaTreasury();
    } catch (e) { toast.error(e.response?.data?.detail || 'File upload failed'); }
  };

  const handleSeedSaTreasury = async () => {
    try { await api.post('/sanctions/sa-treasury/seed-sample'); toast.success('Sample SA Treasury CSV loaded. Run ingestion to apply.'); loadSaTreasury(); }
    catch { toast.error('Seed failed'); }
  };

  const [saStatus, setSaStatus] = useState(null);
  const loadSaTreasury = async () => {
    try { const r = await api.get('/sanctions/sa-treasury/current'); setSaStatus(r.data); } catch {}
  };
  useEffect(() => { loadSaTreasury(); }, []);

  if (loading) return <AdminSubPage title="Sanctions Engine" breadcrumb={['Admin', 'System', 'Sanctions Engine']} backPath="/dashboard/admin/system"><div className="text-center py-16 text-gray-400">Loading...</div></AdminSubPage>;

  return (
    <AdminSubPage title="Sanctions Engine — Configuration" breadcrumb={['Admin', 'System', 'Sanctions Engine']} backPath="/dashboard/admin/system">
      <div className="space-y-5" data-testid="sanctions-config-page">
        <div className="bg-blue-50 border border-blue-200 px-4 py-3 text-xs text-blue-800">
          <strong>Note:</strong> This page is for engine-administration. Compliance officers, tellers, and managers use the <a href="/dashboard/compliance" className="underline font-semibold">Compliance Hub</a> for day-to-day screening operations, recording decisions, and managing PEPs.
        </div>

        <Tabs defaultValue="config">
          <TabsList>
            <TabsTrigger value="config" data-testid="tab-config"><SettingsIcon className="w-3 h-3 mr-1" />Configuration</TabsTrigger>
            <TabsTrigger value="ingest" data-testid="tab-ingest"><Download className="w-3 h-3 mr-1" />Ingestion</TabsTrigger>
            <TabsTrigger value="bureau" data-testid="tab-bureau"><Building2 className="w-3 h-3 mr-1" />Bureau Providers</TabsTrigger>
          </TabsList>

          <TabsContent value="config" className="pt-4">
            <div className="bg-white border border-gray-200 p-6 max-w-2xl space-y-5">
              <div className="flex items-center gap-3">
                <Switch checked={config?.enabled} onCheckedChange={v => setConfig({ ...config, enabled: v })} data-testid="cfg-enabled" />
                <div><Label className="text-sm">Sanctions screening enabled</Label><p className="text-xs text-gray-400">Master switch.</p></div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={config?.auto_screen_on_create} onCheckedChange={v => setConfig({ ...config, auto_screen_on_create: v })} data-testid="cfg-auto" />
                <div><Label className="text-sm">Auto-screen on client creation</Label><p className="text-xs text-gray-400">FIC Act CDD requirement (recommended ON).</p></div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={config?.block_on_match} onCheckedChange={v => setConfig({ ...config, block_on_match: v })} data-testid="cfg-block" />
                <div><Label className="text-sm">Auto-block client on confirmed match</Label><p className="text-xs text-gray-400">Sets <code>status=sanctions_blocked</code> until manager / compliance officer clears.</p></div>
              </div>

              <Button onClick={handleSaveConfig} disabled={saving} className="bg-[#1f1f1f] text-white" data-testid="save-cfg">{saving ? 'Saving...' : 'Save Configuration'}</Button>
            </div>
          </TabsContent>

          <TabsContent value="ingest" className="pt-4 space-y-4">
            {/* Schedule */}
            <div className="bg-white border border-gray-200 p-5 max-w-2xl">
              <div className="flex items-center gap-2 mb-3"><Clock className="w-4 h-4 text-[#C5A059]" /><h3 className="font-semibold">Nightly cron schedule</h3></div>
              <p className="text-xs text-gray-500 mb-3">A background job re-downloads OFAC SDN, UN Consolidated, and SA Treasury daily. Default 00:00 UTC (02:00 SAST).</p>
              <div className="flex items-end gap-2">
                <div><Label className="text-xs">Hour (UTC)</Label><Input type="number" min={0} max={23} value={config?.schedule?.hour ?? 0} onChange={e => setConfig({ ...config, schedule: { ...config.schedule, hour: parseInt(e.target.value || 0) } })} className="w-24" data-testid="schedule-hour" /></div>
                <div><Label className="text-xs">Minute</Label><Input type="number" min={0} max={59} value={config?.schedule?.minute ?? 0} onChange={e => setConfig({ ...config, schedule: { ...config.schedule, minute: parseInt(e.target.value || 0) } })} className="w-24" data-testid="schedule-minute" /></div>
                <Button onClick={handleSaveSchedule} className="bg-[#1f1f1f] text-white" data-testid="save-schedule">Save Schedule</Button>
              </div>
              {config?.schedule?.next_run && <p className="text-xs text-emerald-700 mt-2">Next run: {config.schedule.next_run.slice(0, 16).replace('T', ' ')} UTC</p>}
            </div>

            {/* Run-now */}
            <div className="bg-white border border-gray-200 p-5 max-w-2xl">
              <div className="flex items-center justify-between mb-3">
                <div><h3 className="font-semibold">Run ingestion now</h3><p className="text-xs text-gray-500">Triggers OFAC + UN + SA Treasury immediately.</p></div>
                <div className="flex gap-2">
                  <Button onClick={handleSeedSample} variant="outline" data-testid="seed-btn"><Database className="w-4 h-4 mr-1" />Seed sample</Button>
                  <Button onClick={handleRunIngest} disabled={ingesting} className="bg-[#C5A059] hover:bg-[#D4B16A] text-white" data-testid="run-ingest-btn">{ingesting ? 'Running...' : 'Run now'}</Button>
                </div>
              </div>
              {ingestReport && (
                <div className="text-xs bg-gray-50 border border-gray-200 p-3 mt-2" data-testid="ingest-report">
                  <p>Duration: {ingestReport.duration_seconds}s &middot; Total ingested: <strong>{ingestReport.total_ingested}</strong></p>
                  <ul className="mt-2 space-y-1">
                    {ingestReport.results?.map((r, i) => (
                      <li key={i} className={r.ok ? 'text-emerald-700' : 'text-red-700'}>
                        {r.ok ? '✓' : '✗'} {r.source}: {r.count} entries {r.error && `(${r.error})`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* SA Treasury upload */}
            <div className="bg-white border border-gray-200 p-5 max-w-2xl">
              <h3 className="font-semibold mb-1">SA Treasury — manual list upload</h3>
              <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 mb-3">
                <p className="font-semibold">Where do I get this list?</p>
                <p className="mt-1">SA Treasury / FIC do <strong>not</strong> publish a machine-readable feed. Source the latest "Targeted Financial Sanctions" register from <a href="https://www.fic.gov.za/Documents/Pages/Targeted-Financial-Sanctions-Lists.aspx" target="_blank" rel="noreferrer" className="text-blue-600 underline">fic.gov.za → TFS Lists</a> (PDF) and convert to CSV with these columns:</p>
                <p className="mt-1 font-mono text-[11px] bg-white border border-amber-200 px-2 py-1">full_name,aliases,country,program,id_number,list_date</p>
                <p className="mt-1">Aliases are <code>|</code> (pipe) separated. Click <em>Load sample</em> below if you'd like to test with the public Gupta-family TFS entries.</p>
              </div>
              {saStatus?.present && (
                <div className="bg-emerald-50 border border-emerald-200 p-2 text-xs text-emerald-800 mb-3" data-testid="sa-treasury-current">
                  Currently stored: <strong>{saStatus.filename || 'paste-text'}</strong>{saStatus.is_sample ? ' (sample)' : ''} · uploaded {saStatus.uploaded_at?.slice(0, 16).replace('T', ' ')} UTC
                </div>
              )}
              <div className="flex gap-2 flex-wrap mb-3">
                <label className="px-3 py-1.5 text-xs border border-gray-300 cursor-pointer hover:bg-gray-50" data-testid="sa-csv-file-label">
                  Upload .csv file
                  <input type="file" accept=".csv" className="hidden" onChange={(e) => handleUploadSaTreasuryFile(e.target.files?.[0])} data-testid="sa-csv-file-input" />
                </label>
                <Button variant="outline" size="sm" onClick={handleSeedSaTreasury} data-testid="sa-seed-sample">Load sample</Button>
              </div>
              <p className="text-xs text-gray-500 mb-1">…or paste CSV text directly:</p>
              <textarea value={csvText} onChange={e => setCsvText(e.target.value)} placeholder="full_name,aliases,country,program,id_number,list_date&#10;Atul Gupta,Atul Kumar Gupta|Tony Gupta,ZA,FIC-SA,,2022-10-04" rows={6} className="w-full border border-gray-200 p-2 text-xs font-mono" data-testid="sa-csv" />
              <Button onClick={handleUploadSaTreasury} className="bg-[#1f1f1f] text-white mt-2" data-testid="upload-sa-csv">Upload SA Treasury CSV</Button>
            </div>

            {/* History */}
            <div className="bg-white border border-gray-200 p-5 max-w-3xl">
              <h3 className="font-semibold mb-3">Run history</h3>
              <table className="w-full text-xs">
                <thead className="bg-gray-50"><tr><th className="px-2 py-2 text-left">Started</th><th className="px-2 py-2 text-right">Duration</th><th className="px-2 py-2 text-right">Total</th><th className="px-2 py-2 text-left">Sources</th></tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {history.map(h => (
                    <tr key={h.id}>
                      <td className="px-2 py-2 text-gray-500">{h.started_at?.slice(0, 16).replace('T', ' ')}</td>
                      <td className="px-2 py-2 text-right font-mono">{h.duration_seconds}s</td>
                      <td className="px-2 py-2 text-right font-mono font-bold">{h.total_ingested}</td>
                      <td className="px-2 py-2">{h.results?.map(r => `${r.ok?'✓':'✗'}${r.source}(${r.count})`).join(' · ')}</td>
                    </tr>
                  ))}
                  {history.length === 0 && <tr><td colSpan={4} className="px-2 py-6 text-center text-gray-400">No runs yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="bureau" className="pt-4">
            <div className="bg-white border border-gray-200 p-5 max-w-3xl space-y-5">
              <p className="text-xs text-gray-500">Enable a paid credit-bureau provider to add real-time sanctions / PEP / adverse-media screening on top of the free public lists. <strong>Credentials are managed in <a href="/dashboard/admin/system/external-services" className="underline text-blue-600">External Services</a></strong> — when you save TransUnion or Experian creds there and toggle Active, the engine picks them up automatically.</p>

              <ProviderRow
                name="transunion"
                label="TransUnion (SA Compliance Suite)"
                description="South African PEP, sanctions, adverse media. Provides idMatch / dobMatch flags and detailed PEP classifications."
                status={config?.bureau_status?.transunion}
                checked={config?.providers?.transunion}
                onToggle={v => setConfig({ ...config, providers: { ...config.providers, transunion: v } })}
                docsUrl="https://www.transunion.co.za/business/financial-services/compliance"
                testId="cfg-tu"
              />
              <ProviderRow
                name="experian"
                label="Experian (CrossCore Compliance)"
                description="Global sanctions + PEP + adverse-media via World-Check & ACURIS. Returns explicit match-quality and decision recommendation."
                status={config?.bureau_status?.experian}
                checked={config?.providers?.experian}
                onToggle={v => setConfig({ ...config, providers: { ...config.providers, experian: v } })}
                docsUrl="https://www.experian.co.uk/business/regulation-and-fraud/financial-crime/pep-and-watchlists"
                testId="cfg-experian"
              />

              <Button onClick={handleSaveConfig} disabled={saving} className="bg-[#1f1f1f] text-white" data-testid="save-cfg-bureau">{saving ? 'Saving...' : 'Save Provider Configuration'}</Button>

              <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                <strong>Field mapping (built-in):</strong> The engine automatically translates the bureau's response into the Compliance Hub's normalised hit-card schema (match_quality, ID/DOB match, PEP classification, adverse-media URL, etc.). No further integration work is needed.
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AdminSubPage>
  );
};

const ProviderRow = ({ name, label, description, status, checked, onToggle, docsUrl, testId }) => (
  <div className="flex items-start gap-3 border border-gray-200 p-3">
    <Switch checked={!!checked} onCheckedChange={onToggle} disabled={!status?.credentials_present} data-testid={testId} />
    <div className="flex-1">
      <div className="flex items-center gap-2 flex-wrap">
        <Label className="text-sm font-semibold">{label}</Label>
        <a href={docsUrl} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1">docs <ExternalLink className="w-3 h-3" /></a>
        {status?.credentials_present ? (
          status.is_active ? (
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded">Linked &middot; Active &middot; {status.environment}</span>
          ) : (
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-amber-50 text-amber-700 rounded">Linked &middot; Inactive</span>
          )
        ) : (
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-gray-100 text-gray-600 rounded">No credentials</span>
        )}
      </div>
      <p className="text-xs text-gray-500 mt-1">{description}</p>
      {!status?.credentials_present && (
        <p className="text-xs text-gray-400 mt-1">→ Add credentials at <a href="/dashboard/admin/system/external-services" className="text-blue-600 underline">Admin &gt; System &gt; External Services</a> (service type: <em>Credit Bureau</em>, provider: <em>{label.split(' ')[0]}</em>).</p>
      )}
    </div>
  </div>
);

export default SanctionsPage;
